import type { ActionDefinition } from '@gzl10/nexus-sdk'
import { useTextField } from '@gzl10/nexus-sdk/fields'
import { DEFAULT_MASTER_TYPES } from '../constants.js'

/**
 * Uninstall a master type — removes all records of that type.
 * Cannot uninstall default types (languages, timezones).
 * Only available in development (handler throws ForbiddenError in production).
 */
export const uninstallTypeAction: ActionDefinition = {
  key: 'uninstall-type',
  scope: 'module',
  label: { en: 'Uninstall Master Type', es: 'Desinstalar Tipo de Maestro' },
  icon: 'mdi:database-minus',
  variant: 'danger',
  output: {},

  confirm: {
    type: 'simple',
    title: { en: 'Uninstall Master Type', es: 'Desinstalar Tipo de Maestro' },
    message: {
      en: 'This will delete ALL records of the selected type. This action cannot be undone.',
      es: 'Esto eliminará TODOS los registros del tipo seleccionado. Esta acción no se puede deshacer.'
    },
  },

  input: {
    type: useTextField({
      label: { en: 'Type slug to uninstall', es: 'Slug del tipo a desinstalar' },
      required: true,
      hint: {
        en: 'Enter the master type slug (e.g., "currencies", "countries")',
        es: 'Introduce el slug del tipo (ej: "currencies", "countries")'
      },
    }),
  },

  handler: async (ctx, input) => {
    if (process.env['NODE_ENV'] === 'production') {
      throw new ctx.core.errors.ForbiddenError('Master type management is only available in development')
    }

    const { type } = input as { type: string }

    // Prevent uninstalling default types
    if (DEFAULT_MASTER_TYPES.includes(type as typeof DEFAULT_MASTER_TYPES[number])) {
      throw new ctx.core.errors.AppError(`Cannot uninstall default master type "${type}" (required by core)`, 400)
    }

    // Check if type exists
    const existing = await ctx.db.knex('masters').where({ type }).first()
    if (!existing) {
      throw new ctx.core.errors.NotFoundError(`Master type "${type}" is not installed`)
    }

    const deleted = await ctx.db.knex('masters').where({ type }).del()
    return { uninstalled: type, count: deleted }
  },
}
